import { useQueryClient } from "@tanstack/react-query";
import { Timer, MessageCircle, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { spotCountdown, enquiryDay } from "@/lib/spot";
import { waNumber } from "@/lib/pdf";

const TONE = {
  ok: "border-emerald-200 bg-emerald-50 text-emerald-800",
  soon: "border-amber-300 bg-amber-50 text-amber-900",
  urgent: "border-red-300 bg-red-50 text-red-700 animate-pulse",
  over: "border-slate-200 bg-slate-100 text-slate-500",
};

const spotEnds = (r) => {
  const e = new Date(enquiryDay(r) + "T00:00:00");
  e.setDate(e.getDate() + 5);
  return e.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
};

const remindText = (r, c) => {
  const i = r.inputs;
  const when = c.left === 0 ? `*today (${spotEnds(r)})*` : `*${spotEnds(r)}*`;
  return `Dear ${i.parentName || "Parent"},\n\nThis is a gentle reminder from Indo Scots Global School. ` +
    `The Spot admission benefit for ${i.childName || "your child"} (${i.grade}) ends on ${when}.\n\n` +
    "Please complete the admission fee payment by then to keep the Spot discount." +
    (i.counselorMobile ? `\n\nFor any queries, call or WhatsApp your counsellor ${i.counselorName} on ${i.counselorMobile}.` : "") +
    "\n\n*Thanks & Regards,*\nIndo Scots Global School, Pune";
};

const ago = (iso) => {
  const dt = new Date(iso);
  const today = new Date().toDateString() === dt.toDateString();
  return today ? `today ${dt.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}` : dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

export const SpotCountdown = ({ r, testid, remind = false }) => {
  const qc = useQueryClient();
  const c = spotCountdown(r);
  if (!c) return null;
  const canRemind = remind && c.left >= 0 && r.inputs?.contact;
  const log = r.spot_reminders || [];
  const last = log[log.length - 1];
  const onRemind = () => {
    api.post(`/receipts/${r.id}/spot-reminded`).then((res) => {
      qc.setQueryData(["receipt", r.id], res.data);
      qc.invalidateQueries({ queryKey: ["receipts"] });
    }).catch(() => {});
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TONE[c.tone]}`} data-testid={testid} data-left={c.left}>
        <Timer className="h-3 w-3" />{c.label}
      </span>
      {canRemind && (
        <a href={`https://wa.me/${waNumber(r.inputs.contact)}?text=${encodeURIComponent(remindText(r, c))}`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-800 hover:bg-green-100"
          onClick={onRemind} data-testid={`${testid}-remind`} title="Remind the parent on WhatsApp">
          <MessageCircle className="h-3 w-3" />{last ? "Remind again" : "Remind parent"}
        </a>
      )}
      {remind && last && (
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-600" data-testid={`${testid}-reminded`}
          title={log.map((x) => `${new Date(x.at).toLocaleString("en-IN")} · ${x.by}`).join("\n")}>
          <CheckCheck className="h-3.5 w-3.5 text-green-700" />Reminded {log.length > 1 ? `${log.length}× · last ` : ""}{ago(last.at)} by {last.by}
        </span>
      )}
    </span>
  );
};
